import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  createAnnouncement,
  updateAnnouncement,
  removeAnnouncement,
  getAnnouncements,
} from "../../../services/announcements";

function useAnnouncement(
  { page = 1, limit = 10 } = {},
  announcementId = null,
  onSuccess = null
) {
  const queryClient = useQueryClient();

  // Get all announcements query (with pagination)
  const {
    data: announcements = {
      announcements: [],
      page: 1,
      limit: 10,
      totalPages: 1,
      total: 0,
    },
    isLoading: isGettingAnnouncements,
    error: announcementsError,
    refetch: refetchAnnouncements,
  } = useQuery({
    queryKey: ["announcements", page, limit],
    queryFn: () => getAnnouncements({ page, limit }),
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get single announcement query
  const {
    data: announcement,
    isLoading: isGettingAnnouncement,
    error: announcementError,
  } = useQuery({
    queryKey: ["announcement", announcementId],
    queryFn: () =>
      getAnnouncements({ page: 1, limit: 1000 }).then((data) =>
        data.announcements.find((ann) => ann._id === announcementId)
      ),
    enabled: !!announcementId,
  });

  // Create announcement mutation
  const { mutate: createAnnouncementFn, isLoading: isCreatingAnnouncement } =
    useMutation({
      mutationFn: (announcementData) => {
        return createAnnouncement(announcementData);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["announcements"]);
        toast.success("Announcement created successfully");
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.error || "Failed to create announcement"
        );
      },
    });

  // Update announcement mutation
  const { mutate: updateAnnouncementFn, isLoading: isUpdatingAnnouncement } =
    useMutation({
      mutationFn: ({ announcementId, announcementData }) => {
        return updateAnnouncement(announcementId, announcementData);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["announcements"]);
        queryClient.invalidateQueries(["announcement"]);
        toast.success("Announcement updated successfully");
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.error || "Failed to update announcement"
        );
      },
    });

  // Delete announcement mutation
  const { mutate: deleteAnnouncementFn, isLoading: isDeletingAnnouncement } =
    useMutation({
      mutationFn: (announcementId) => {
        return removeAnnouncement(announcementId);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["announcements"]);
        toast.success("Announcement deleted successfully");
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.error || "Failed to delete announcement"
        );
      },
    });

  return {
    // Queries
    announcements,
    isGettingAnnouncements,
    announcementsError,
    announcement,
    isGettingAnnouncement,
    announcementError,
    refetchAnnouncements,

    // Mutations
    createAnnouncement: createAnnouncementFn,
    isCreatingAnnouncement,
    updateAnnouncement: updateAnnouncementFn,
    isUpdatingAnnouncement,
    deleteAnnouncement: deleteAnnouncementFn,
    isDeletingAnnouncement,

    // Pagination info
    page: announcements.page,
    limit: announcements.limit,
    totalPages: announcements.totalPages,
    total: announcements.total,

    // Loading states
    isLoading:
      isGettingAnnouncements ||
      isCreatingAnnouncement ||
      isUpdatingAnnouncement ||
      isDeletingAnnouncement,
  };
}

export default useAnnouncement;
